'use strict';
__webpack_public_path__ = __webpack_public_path__ + 'js/'; // Webpack public path for dynamic imports
import { mediaQuery, getMediaQuery } from '@jspartials/general-utils';
import { initCarousel } from '@jsproduct/detailsPage/carouselMultimedia';
import { initLazyVideos } from '@jscomponents/video-lazy';
import { listenProductColorVariants } from '@jsproduct/detailsPage/colorVariants';
import {
  listenSizeVariantButtons,
  closeSizeVariants
} from '@jsproduct/detailsPage/sizeVariants';
import { listenAddToCartButton } from '@jsproduct/detailsPage/addToCart';
import { initHistoryPpd } from '@jsproduct/detailsPage/history';

// Variables
const mediaQueryTablet = getMediaQuery(mediaQuery.tablet);
const eventReloadVtoButtons = new CustomEvent('reloadVtoButtons');

// Non-critical modules, loaded on demand instead of bundled in the initial
// PDP script. init() re-runs on every color swap and breakpoint change;
// webpack caches the chunk after the first import() so re-calling these is
// cheap. carouselMultimedia, colorVariants, sizeVariants, addToCart and
// history stay static imports above: they're needed immediately (or, for
// history, already pulled in by colorVariants' circular import so
// deferring it wouldn't save anything).
const loadModal = () =>
  import(/* webpackChunkName: "pdp-modal" */ '@jsclasses/modal-class');

const loadGtmUtils = () =>
  import(/* webpackChunkName: "pdp-gtm" */ '@jspartials/gtm-utils');

const loadBottomPadding = () =>
  import(
    /* webpackChunkName: "pdp-bottom-padding" */ '@jsproduct/detailsPage/handleBottomPadding'
  ).then(({ initBottomPadding }) => initBottomPadding());

const loadRecommendations = () =>
  import(
    /* webpackChunkName: "pdp-recommendations" */ '@jsproduct/detailsPage/recommendations'
  ).then(({ initRecommendations }) => initRecommendations());

const loadFoursixty = () =>
  import(
    /* webpackChunkName: "pdp-foursixty" */ '@jsproduct/detailsPage/foursixty'
  ).then(({ initFoursixty }) => initFoursixty());

const loadTrustPilot = () =>
  import(
    /* webpackChunkName: "pdp-trustpilot" */ '@jsproduct/detailsPage/trustpilot'
  ).then(({ initTrustPilot }) => initTrustPilot());

const loadWishlist = () =>
  import(
    /* webpackChunkName: "pdp-wishlist" */ '@jsproduct/detailsPage/wishlist'
  ).then(({ initWishlistButtonsListeners }) => initWishlistButtonsListeners());

const loadFastSignUpForm = () =>
  import(
    /* webpackChunkName: "pdp-fast-signup-form" */ '@jsproduct/detailsPage/fastSignUpForm'
  ).then(({ listenFastSignUpForm }) => listenFastSignUpForm());

const loadProductDetails = () =>
  import(
    /* webpackChunkName: "pdp-product-details" */ '@jsproduct/detailsPage/productDetails'
  );

const loadDesktopZoom = () =>
  import(
    /* webpackChunkName: "pdp-desktop-zoom" */ '@jsproduct/detailsPage/desktopZoom'
  ).then(({ startZoom }) => startZoom());

const loadShippingMethodMessage = () =>
  import(
    /* webpackChunkName: "pdp-shipping-method-message" */ '@jsproduct/detailsPage/shippingMethodMessage'
  ).then(({ initShippingMethodMessages }) => initShippingMethodMessages());

const loadMinLastPriceTooltip = () =>
  import(
    /* webpackChunkName: "pdp-min-last-price" */ '@jsproduct/detailsPage/minLastPrice'
  ).then(({ initMinLastPriceTooltip }) => initMinLastPriceTooltip());

const loadInstoreAvailability = () =>
  import(
    /* webpackChunkName: "pdp-instore-availability" */ '@jsproduct/detailsPage/instoreAvailability'
  ).then(({ initInstoreAvailability }) => initInstoreAvailability());

const loadKlarnaMessaging = () =>
  import(
    /* webpackChunkName: "pdp-klarna-messaging" */ '@jscomponents/klarnaMessaging'
  ).then(({ initKlarnaMessagingPdp }) => initKlarnaMessagingPdp());

/**
 * Initializes the product detail page scripts
 * @returns {void}
 */
const init = () => {
  if (!mediaQueryTablet.matches) {
    initCarousel();
    loadProductDetails().then(({ removeRightColumnScrollBehaviour }) =>
      removeRightColumnScrollBehaviour()
    );
  } else {
    initLazyVideos();
    loadProductDetails().then(({ addRightColumnScrollBehaviour }) =>
      addRightColumnScrollBehaviour()
    );
  }

  listenSizeVariantButtons();

  listenAddToCartButton();

  loadRecommendations();

  loadFoursixty();

  loadTrustPilot();

  loadWishlist();

  loadFastSignUpForm();

  loadProductDetails().then(({ initAccordionsBehaviour }) =>
    initAccordionsBehaviour()
  );

  loadDesktopZoom();

  loadShippingMethodMessage();

  loadMinLastPriceTooltip();

  // Site in-store: inerte si la PDP no lleva el contenedor del aviso.
  loadInstoreAvailability();

  loadKlarnaMessaging();

  loadBottomPadding();
};

// Listeners

// Resize
mediaQueryTablet.addEventListener('change', function (mm) {
  if (!mm.matches) {
    initCarousel();
    loadProductDetails().then(({ removeRightColumnScrollBehaviour }) =>
      removeRightColumnScrollBehaviour()
    );
  } else {
    closeSizeVariants();
    loadProductDetails().then(({ addRightColumnScrollBehaviour }) =>
      addRightColumnScrollBehaviour()
    );
  }
});

// After update content
document.addEventListener('afterUpdateContentOfProductDetailPage', function () {
  init();
  initLazyVideos();
  document.dispatchEvent(eventReloadVtoButtons);
  loadModal().then(({ default: Modal }) => Modal.initElements('pdp'));

  const gtmProduct = document.querySelector('.gtm-product');
  loadGtmUtils().then(({ gtmLaunchProductDetail, gtmListenProductVariants }) => {
    if (gtmProduct) {
      gtmLaunchProductDetail(gtmProduct.dataset.pid);
    }
    gtmListenProductVariants();
  });
});

// DOMContentLoaded
document.addEventListener('DOMContentLoaded', function () {
  const gtmProduct = document.querySelector('.gtm-product');

  loadGtmUtils().then(({ gtmLaunchProductDetail, gtmListenProductVariants }) => {
    if (gtmProduct) {
      gtmLaunchProductDetail(gtmProduct.dataset.pid);
    }

    gtmListenProductVariants();
  });

  loadModal().then(({ default: Modal }) => Modal.initElements('pdp'));

  listenProductColorVariants();

  init();

  initHistoryPpd();
});

